import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useTable, useSortBy, useGlobalFilter, usePagination } from 'react-table';
import { useHistory, useParams } from 'react-router-dom';
import { Badge, Spinner, Button, Alert } from 'react-bootstrap';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { NoData } from '../../../components/SharedComponents/NoData';
import PageTitle from '../../../layouts/PageTitle';
import { GlobalFilter } from './GlobalFilter';

// Lucide React Icons
import {
  Plus,
  RefreshCw,
  Loader2,
  FileText,
  CreditCard,
  Link,
  ArrowRight,
  ArrowLeft
} from 'lucide-react';

export const InvoiceList = () => {
  const { companyId } = useParams();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [typeFilter, setTypeFilter] = useState('all'); // 'all', 'invoice', 'credit_note'

  const history = useHistory();

  // Helper function to format dates consistently
  const formatDate = (dateValue) => {
    if (!dateValue) return '-';

    // If it's already in YYYY-MM-DD format, return as is
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }

    // If it's an ISO string or Date object, format it
    try {
      const date = new Date(dateValue);
      return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD
    } catch (error) {
      console.warn('Invalid date format:', dateValue);
      return dateValue || '-';
    }
  };

  // Helper function to find relationships between invoices
  const getRelationshipInfo = (invoice, allInvoices) => {
    const relationships = [];

    // If this is a credit note, find its related invoice
    if (invoice.invoice_type === 'credit_note' && invoice.related_invoice_id) {
      const relatedInvoice = allInvoices.find(inv => inv.id === invoice.related_invoice_id);
      if (relatedInvoice) {
        relationships.push({
          type: 'credit_relates_to_invoice',
          relatedInvoice: relatedInvoice,
          description: `Krediterar ${relatedInvoice.invoicenumber || 'faktura'}`
        });
      } else if (invoice.related_invoice_details) {
        // Use the details from API if invoice not found in current list
        relationships.push({
          type: 'credit_relates_to_invoice',
          relatedInvoice: invoice.related_invoice_details,
          description: `Krediterar ${invoice.related_invoice_details.invoice_number}`
        });
      }
    }

    // If this is an invoice, find credit notes that relate to it
    if (invoice.invoice_type === 'invoice') {
      const relatedCreditNotes = allInvoices.filter(inv =>
        inv.invoice_type === 'credit_note' && inv.related_invoice_id === invoice.id
      );

      relatedCreditNotes.forEach(creditNote => {
        relationships.push({
          type: 'invoice_has_credit_note',
          relatedInvoice: creditNote,
          description: `Krediterad av ${creditNote.invoicenumber || 'kreditnota'}`
        });
      });
    }

    return relationships;
  };

  const fetchInvoiceData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Get all invoices
      const getInvoicesApiUrl = getApiUrl(`${companyId}/invoices`);
      const invoicesResult = await axios.get(getInvoicesApiUrl, { withCredentials: true });

      console.log('🔍 DEBUG - Raw API Response:', {
        count: invoicesResult.data.data.length,
        firstInvoice: invoicesResult.data.data[0],
        sampleFields: invoicesResult.data.data[0] ? Object.keys(invoicesResult.data.data[0]) : []
      });

      // Fetch detailed data for each invoice in parallel
      const detailPromises = invoicesResult.data.data.map(async (invoice) => {
        try {
          // Fetch detailed invoice data and status history in parallel
          const [detailResponse, statusResponse] = await Promise.allSettled([
            axios.get(getApiUrl(`${companyId}/invoices/${invoice.id}`), { withCredentials: true }),
            axios.get(getApiUrl(`${companyId}/invoices/${invoice.id}/status-history`), { withCredentials: true })
          ]);

          // Get detailed data if successful
          let detailedInvoice = invoice;
          if (detailResponse.status === 'fulfilled') {
            detailedInvoice = {
              ...invoice, // Keep original data
              ...detailResponse.value.data.data, // Add detailed data
              id: invoice.id // Ensure ID is preserved
            };
            console.log(`🔍 DEBUG - Detailed API response for ${invoice.id}:`, {
              rawDetailedData: detailResponse.value.data.data,
              totals: detailResponse.value.data.data.totals,
              hasGrandTotal: detailResponse.value.data.data.totals?.grand_total !== undefined
            });
          }

          // Get status history if successful
          let latestStatus = detailedInvoice.status;
          if (statusResponse.status === 'fulfilled') {
            const statusHistory = statusResponse.value.data?.data || [];
            if (statusHistory.length > 0) {
              const sortedHistory = [...statusHistory].sort((a, b) =>
                new Date(b.changed_at) - new Date(a.changed_at)
              );
              latestStatus = sortedHistory[0].status;
            }
          }

          const finalInvoice = {
            ...detailedInvoice,
            status: latestStatus
          };

          console.log(`🔍 DEBUG - Final invoice data for ${invoice.id}:`, {
            id: finalInvoice.id,
            invoicenumber: finalInvoice.invoicenumber,
            invoice_type: finalInvoice.invoice_type,
            is_published: finalInvoice.is_published,
            amountFields: {
              // From list API
              total_with_tax: finalInvoice.total_with_tax,
              total_sum: finalInvoice.total_sum,
              tax_amount: finalInvoice.tax_amount,
              // From detailed API
              totals: finalInvoice.totals,
              'totals.grand_total': finalInvoice.totals?.grand_total,
              'totals.subtotal': finalInvoice.totals?.subtotal
            },
            status: finalInvoice.status
          });

          return finalInvoice;
        } catch (error) {
          console.error(`Failed to fetch details for invoice ${invoice.id}:`, error);
          return { ...invoice, status: invoice.status };
        }
      });

      const processedInvoices = await Promise.all(detailPromises);

      setInvoices(processedInvoices);
    } catch (err) {
      setError('Det gick inte att hämta fakturorna. Försök igen senare.');
      console.error('Error fetching invoice data:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [companyId]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchInvoiceData();
  };

  useEffect(() => {
    fetchInvoiceData();
  }, [fetchInvoiceData]);

  const handleRowClick = (row) => {
    console.log('🔍 DEBUG - Row click:', {
      rowOriginal: row.original,
      id: row.original.id,
      is_published: row.original.is_published,
      invoice_type: row.original.invoice_type
    });

    if (row.original.is_published) {
      history.push(`/${companyId}/view-invoice-new/${row.original.id}`);
    } else if (row.original.invoice_type === 'invoice') {
      history.push(`/${companyId}/edit-invoice/${row.original.id}`);
    } else if (row.original.invoice_type === 'credit_note') {
      history.push(`/${companyId}/edit-credit-note/${row.original.id}`);
    }
  };

  // Filter invoices based on type
  const filteredInvoices = useMemo(() => {
    if (typeFilter === 'all') return invoices;
    return invoices.filter(invoice => invoice.invoice_type === typeFilter);
  }, [invoices, typeFilter]);

  // Calculate comprehensive financial metrics
  const financialMetrics = useMemo(() => {
    const publishedInvoices = invoices.filter(inv => inv.is_published);
    const publishedRegularInvoices = publishedInvoices.filter(inv => inv.invoice_type === 'invoice');
    const publishedCreditNotes = publishedInvoices.filter(inv => inv.invoice_type === 'credit_note');

    // Calculate amounts with correct field mapping from API responses
    const getAmount = (inv) => {
      // Priority order based on actual API structure:
      // 1. totals.grand_total (from detailed API call)
      // 2. total_with_tax (from list API)
      // 3. Fallbacks for other possible fields
      let amount = 0;

      if (inv.totals && inv.totals.grand_total !== undefined) {
        amount = inv.totals.grand_total;
      } else if (inv.total_with_tax !== undefined) {
        amount = inv.total_with_tax;
      } else if (inv.grand_total !== undefined) {
        amount = inv.grand_total;
      } else if (inv.total_amount !== undefined) {
        amount = inv.total_amount;
      }

      const parsedAmount = parseFloat(amount) || 0;

      console.log(`🔍 DEBUG - Amount calculation for ${inv.id}:`, {
        totals_grand_total: inv.totals?.grand_total,
        total_with_tax: inv.total_with_tax,
        grand_total: inv.grand_total,
        total_amount: inv.total_amount,
        selectedAmount: amount,
        parsedAmount: parsedAmount,
        invoiceNumber: inv.invoicenumber,
        invoiceType: inv.invoice_type
      });

      return parsedAmount;
    };

    const totalInvoiceAmount = publishedRegularInvoices.reduce((sum, inv) => sum + getAmount(inv), 0);
    const totalCreditAmount = publishedCreditNotes.reduce((sum, inv) => sum + getAmount(inv), 0);
    const netRevenue = totalInvoiceAmount - totalCreditAmount;

    // Status-based metrics
    const paidInvoices = publishedInvoices.filter(inv => inv.status === 'Paid');
    const overdueInvoices = publishedInvoices.filter(inv => inv.status === 'Overdue');
    const pendingInvoices = publishedInvoices.filter(inv => inv.status === 'Published');

    const paidAmount = paidInvoices.reduce((sum, inv) => sum + getAmount(inv), 0);
    const overdueAmount = overdueInvoices.reduce((sum, inv) => sum + getAmount(inv), 0);
    const pendingAmount = pendingInvoices.reduce((sum, inv) => sum + getAmount(inv), 0);

    // Draft metrics
    const draftCount = invoices.filter(inv => !inv.is_published).length;

    return {
      totalInvoiceAmount,
      totalCreditAmount,
      netRevenue,
      paidAmount,
      overdueAmount,
      pendingAmount,
      publishedCount: publishedInvoices.length,
      paidCount: paidInvoices.length,
      overdueCount: overdueInvoices.length,
      pendingCount: pendingInvoices.length,
      draftCount,
      collectionRate: totalInvoiceAmount > 0 ? (paidAmount / totalInvoiceAmount) * 100 : 0,
      getAmount // Export the helper function
    };
  }, [invoices]);

  // Calculate totals for filtered invoices
  const filteredTotals = useMemo(() => {
    const publishedInvoices = filteredInvoices.filter(inv => inv.is_published);
    const totalSum = publishedInvoices.reduce((sum, inv) => sum + financialMetrics.getAmount(inv), 0);
    const invoiceCount = filteredInvoices.filter(inv => inv.invoice_type === 'invoice').length;
    const creditNoteCount = filteredInvoices.filter(inv => inv.invoice_type === 'credit_note').length;

    return {
      totalSum,
      totalCount: filteredInvoices.length,
      invoiceCount,
      creditNoteCount,
      publishedCount: publishedInvoices.length
    };
  }, [filteredInvoices, financialMetrics]);

  const COLUMNS = useMemo(() => [
    {
      Header: 'Fakturanummer',
      accessor: 'invoicenumber',
      Cell: ({ value, row }) => {
        console.log('🔍 DEBUG - Fakturanummer Cell:', {
          value,
          isDraft: !row.original.is_published,
          isCredit: row.original.invoice_type === 'credit_note',
          id: row.original.id
        });

        const isDraft = !row.original.is_published;
        const isCredit = row.original.invoice_type === 'credit_note';
        const relationships = getRelationshipInfo(row.original, invoices);

        return (
          <div className="d-flex align-items-center">
            <div
              className="me-3 d-flex align-items-center justify-content-center"
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                backgroundColor: isCredit ? '#f59e0b15' : '#3b82f615',
                color: isCredit ? '#f59e0b' : '#3b82f6'
              }}
            >
              {isCredit ? (
                <CreditCard size={18} />
              ) : (
                <FileText size={18} />
              )}
            </div>
            <div className="flex-grow-1">
              <div className="d-flex align-items-center gap-2">
                <span style={{ fontWeight: '600', fontSize: '0.875rem', color: '#3b82f6' }}>
                  {isDraft ? '-' : (value || '-')}
                </span>
                {relationships.length > 0 && (
                  <div
                    className="relationship-counter"
                    title={relationships.map(r => r.description).join(', ')}
                  >
                    <Link size={10} />
                    <span>{relationships.length}</span>
                  </div>
                )}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                {isCredit ? 'Kreditfaktura' : 'Faktura'}
                {!isDraft && row.original.invoicedate && ` • ${formatDate(row.original.invoicedate)}`}
                {relationships.length > 0 && (
                  <div style={{ fontSize: '0.6875rem', color: '#9ca3af', marginTop: '0.125rem' }}>
                    {relationships[0].description}
                    {relationships.length > 1 && ` (+${relationships.length - 1} mer)`}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      Header: 'Relation',
      accessor: 'related_invoice_id',
      Cell: ({ value, row }) => {
        const relationships = getRelationshipInfo(row.original, invoices);

        if (relationships.length === 0) {
          return <span style={{ fontSize: '0.875rem', color: '#9ca3af' }}>-</span>;
        }

        return (
          <div>
            {relationships.map((relationship, index) => (
              <div
                key={index}
                className="d-flex align-items-center gap-2 mb-1"
                style={{ fontSize: '0.75rem' }}
              >
                {relationship.type === 'credit_relates_to_invoice' && (
                  <>
                    <div
                      className="d-flex align-items-center gap-1"
                      style={{
                        color: '#f59e0b',
                        padding: '0.125rem 0.375rem',
                        backgroundColor: '#fef3c7',
                        borderRadius: '4px',
                        border: '1px solid #fbbf24'
                      }}
                    >
                      <ArrowRight size={10} />
                      <span>Krediterar</span>
                    </div>
                    <span style={{ color: '#374151', fontWeight: '500' }}>
                      {relationship.relatedInvoice.invoice_number || relationship.relatedInvoice.invoicenumber || 'Faktura'}
                    </span>
                  </>
                )}

                {relationship.type === 'invoice_has_credit_note' && (
                  <>
                    <div
                      className="d-flex align-items-center gap-1"
                      style={{
                        color: '#dc2626',
                        padding: '0.125rem 0.375rem',
                        backgroundColor: '#fee2e2',
                        borderRadius: '4px',
                        border: '1px solid #f87171'
                      }}
                    >
                      <ArrowLeft size={10} />
                      <span>Krediterad</span>
                    </div>
                    <span style={{ color: '#374151', fontWeight: '500' }}>
                      {relationship.relatedInvoice.invoicenumber || 'Kreditnota'}
                    </span>
                  </>
                )}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      Header: 'Kund',
      accessor: 'name',
      Cell: ({ value }) => (
        <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>
          {value || '-'}
        </span>
      ),
    },
    {
      Header: 'Fakturadatum',
      accessor: 'invoicedate',
      Cell: ({ value }) => (
        <span style={{ fontSize: '0.875rem' }}>
          {formatDate(value)}
        </span>
      ),
    },
    {
      Header: 'Förfallodatum',
      accessor: 'deadline',
      Cell: ({ value }) => (
        <span style={{ fontSize: '0.875rem' }}>
          {formatDate(value)}
        </span>
      ),
    },
    {
      Header: 'Belopp',
      accessor: 'total_with_tax', // Changed from 'total_amount' to correct field
      Cell: ({ value, row }) => {
        const amount = financialMetrics.getAmount(row.original);
        const isDraft = !row.original.is_published;

        if (isDraft) {
          return <span style={{ fontSize: '0.875rem', color: '#9ca3af' }}>-</span>;
        }

        return (
          <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
            {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount)}
          </span>
        );
      },
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ value, row }) => {
        const getStatusConfig = (status) => {
          switch (status) {
            case 'Draft':
              return { bg: '#f3f4f6', color: '#374151', text: 'Utkast' };
            case 'Published':
              return { bg: '#dbeafe', color: '#1e40af', text: 'Publicerad' };
            case 'Paid':
              return { bg: '#d1fae5', color: '#065f46', text: 'Betalad' };
            case 'Overdue':
              return { bg: '#fee2e2', color: '#991b1b', text: 'Förfallen' };
            case 'Cancelled':
              return { bg: '#f3f4f6', color: '#374151', text: 'Annullerad' };
            default:
              return { bg: '#f3f4f6', color: '#374151', text: value };
          }
        };

        const config = getStatusConfig(value);

        return (
          <span
            style={{
              fontSize: '0.75rem',
              fontWeight: '500',
              padding: '0.25rem 0.75rem',
              borderRadius: '1rem',
              backgroundColor: config.bg,
              color: config.color,
            }}
          >
            {config.text}
          </span>
        );
      },
    }
  ], []);

  const tableInstance = useTable(
    {
      columns: COLUMNS,
      data: filteredInvoices,
      initialState: { pageSize },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    gotoPage,
    pageCount,
    setPageSize: setTablePageSize,
    setGlobalFilter,
  } = tableInstance;

  // Debug pagination and data
  console.log('🔍 DEBUG - Pagination Data:', {
    totalInvoices: invoices.length,
    currentPageData: page.length,
    pageIndex: state.pageIndex,
    pageSize: state.pageSize,
    globalFilter: state.globalFilter,
    invoicesData: invoices.map(inv => ({
      id: inv.id,
      invoicenumber: inv.invoicenumber,
      invoice_type: inv.invoice_type,
      is_published: inv.is_published,
      // Debug amount fields
      total_amount: inv.total_amount,
      amount: inv.amount,
      total: inv.total,
      sum: inv.sum,
      availableFields: Object.keys(inv).filter(key => key.toLowerCase().includes('amount') || key.toLowerCase().includes('total') || key.toLowerCase().includes('sum'))
    })),
    currentPageRows: page.map(row => ({
      id: row.original.id,
      invoicenumber: row.original.invoicenumber,
      invoice_type: row.original.invoice_type,
      is_published: row.original.is_published
    }))
  });

  const { globalFilter, pageIndex } = state;

  // Enhanced pagination controls
  const PaginationControls = () => (
    <div
      style={{
        backgroundColor: '#fff',
        borderTop: '1px solid #f1f5f9',
        padding: '1rem 1.5rem'
      }}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <select
            className="form-select form-select-sm"
            style={{
              width: 'auto',
              fontSize: '0.875rem',
              border: '1px solid #e5e7eb',
              borderRadius: '6px'
            }}
            value={pageSize}
            onChange={e => {
              setPageSize(Number(e.target.value));
              setTablePageSize(Number(e.target.value));
            }}
          >
            {[10, 20, 30, 40, 50].map(size => (
              <option key={size} value={size}>{size} per sida</option>
            ))}
          </select>
          <span style={{ fontSize: '0.875rem', color: '#64748b' }}>
            Visar {page.length} av {filteredInvoices.length} {typeFilter === 'all' ? 'fakturor' : typeFilter === 'invoice' ? 'fakturor' : 'kreditnotor'}
          </span>
        </div>

        <div className="d-flex align-items-center gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => gotoPage(0)}
            disabled={!canPreviousPage}
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          >
            {'<<'}
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
            style={{ fontSize: '0.75rem' }}
          >
            Föregående
          </Button>
          <span style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>
            Sida {pageIndex + 1} av {pageOptions.length}
          </span>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => nextPage()}
            disabled={!canNextPage}
            style={{ fontSize: '0.75rem' }}
          >
            Nästa
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage}
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          >
            {'>>'}
          </Button>
        </div>
      </div>
    </div>
  );

  if (loading && !refreshing) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Laddar...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .table td {
          vertical-align: middle !important;
        }
        
        .clickable-row {
          cursor: pointer;
        }
        
        .clickable-row:hover {
          background-color: #f8f9fa;
        }
        
        .btn-outline-secondary {
          border-color: #6c757d !important;
        }
        
        .btn-outline-secondary:hover {
          background-color: #6c757d !important;
          border-color: #6c757d !important;
          color: #fff !important;
        }
        
        /* Add a refresh button animation */
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .spin {
          animation: spin 1s linear infinite;
        }

        /* Relationship indicators */
        .relationship-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.125rem 0.375rem;
          border-radius: 4px;
          font-size: 0.6875rem;
          font-weight: 500;
          transition: all 0.2s ease;
        }

        .relationship-badge:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .relationship-counter {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.125rem 0.375rem;
          background-color: #f3f4f6;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          font-size: 0.6875rem;
          color: #6b7280;
          transition: all 0.2s ease;
        }

        .relationship-counter:hover {
          background-color: #e5e7eb;
          border-color: #d1d5db;
        }

        /* Stats Grid - Inspired by InvoiceDetailBootstrap */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.25rem;
        }
        
        .stat-card {
          background: white;
          border: none;
          border-radius: 8px;
          padding: 1.5rem;
          text-align: center;
          transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          position: relative;
          overflow: hidden;
        }

        .stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, var(--accent-color, #3b82f6), var(--accent-color-light, #60a5fa));
        }
        
        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
        }
        
        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
          font-size: 1.125rem;
          position: relative;
        }

        .stat-icon::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 12px;
          background: inherit;
          opacity: 0.1;
        }
        
        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 0.375rem;
          color: #1f2937;
        }
        
        .stat-label {
          color: #6b7280;
          font-size: 0.75rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 0.25rem;
        }

        .stat-subtitle {
          color: #9ca3af;
          font-size: 0.6875rem;
          font-weight: 400;
        }
        
        @media (max-width: 1024px) {
          .stats-grid {
            grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          }
        }

        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: 1fr 1fr;
          }
        }
      `}</style>
      <PageTitle activeMenu="Kundfakturor" motherMenu="Fakturor" />
      <div className="col-12">
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        {invoices.length === 0 && !loading ? (
          <>
            <NoData message="Det finns inga fakturor att visa. Skapa en för att kunna se dem här." />
            <div className="d-flex justify-content-center p-4">
              <Button variant="primary" onClick={() => history.push(`/${companyId}/create-invoices`)}>
                <Plus size={16} className="me-2" />
                Skapa ny faktura
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* CFO Financial Dashboard - Inspired by InvoiceDetailBootstrap */}
            <div className="stats-grid mb-4">
              {/* Net Revenue Card */}
              <div className="stat-card" style={{ '--accent-color': '#0284c7', '--accent-color-light': '#0ea5e9' }}>
                <div className="stat-icon" style={{ background: '#f0f9ff', color: '#0284c7' }}>
                  <FileText size={24} />
                </div>
                <div className="stat-value" style={{ color: '#0284c7' }}>
                  {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(financialMetrics.netRevenue)}
                </div>
                <div className="stat-label">Nettointäkter</div>
                <div className="stat-subtitle">
                  Fakturor - Kreditnotor
                </div>
              </div>

              {/* Collection Rate Card */}
              <div className="stat-card" style={{ '--accent-color': '#059669', '--accent-color-light': '#10b981' }}>
                <div className="stat-icon" style={{ background: '#f0fdf4', color: '#059669' }}>
                  <CreditCard size={24} />
                </div>
                <div className="stat-value" style={{ color: '#059669' }}>
                  {financialMetrics.collectionRate.toFixed(1)}%
                </div>
                <div className="stat-label">Indrivningsgrad</div>
                <div className="stat-subtitle">
                  {financialMetrics.paidCount} av {financialMetrics.publishedCount} betalda
                </div>
              </div>

              {/* Outstanding Amount Card */}
              <div className="stat-card" style={{ '--accent-color': '#d97706', '--accent-color-light': '#f59e0b' }}>
                <div className="stat-icon" style={{ background: '#fffbeb', color: '#d97706' }}>
                  <Loader2 size={24} />
                </div>
                <div className="stat-value" style={{ color: '#d97706' }}>
                  {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(financialMetrics.pendingAmount)}
                </div>
                <div className="stat-label">Utestående</div>
                <div className="stat-subtitle">
                  {financialMetrics.pendingCount} publicerade fakturor
                </div>
              </div>

              {/* Overdue Amount Card */}
              <div className="stat-card" style={{ '--accent-color': '#dc2626', '--accent-color-light': '#ef4444' }}>
                <div className="stat-icon" style={{ background: '#fef2f2', color: '#dc2626' }}>
                  <RefreshCw size={24} />
                </div>
                <div className="stat-value" style={{ color: '#dc2626' }}>
                  {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(financialMetrics.overdueAmount)}
                </div>
                <div className="stat-label">Förfallna</div>
                <div className="stat-subtitle">
                  {financialMetrics.overdueCount} förfallna fakturor
                </div>
              </div>
            </div>

            {/* Enhanced header with filters and actions */}
            <div className="d-flex align-items-center justify-content-between mb-4">
              {/* Type Filter and Stats */}
              <div className="d-flex align-items-center gap-4">
                <div className="d-flex align-items-center gap-2">
                  <span style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '500' }}>Visa:</span>
                  <select
                    className="form-select form-select-sm"
                    style={{
                      width: 'auto',
                      fontSize: '0.875rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px'
                    }}
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                  >
                    <option value="all">Alla ({filteredTotals.totalCount})</option>
                    <option value="invoice">Fakturor ({filteredTotals.invoiceCount})</option>
                    <option value="credit_note">Kreditnotor ({filteredTotals.creditNoteCount})</option>
                  </select>
                </div>

                {/* Summary Stats */}
                <div className="d-flex align-items-center gap-3">
                  <div className="px-3 py-2 rounded" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Publicerade:</span>
                    <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1e293b', marginLeft: '0.5rem' }}>
                      {filteredTotals.publishedCount}
                    </span>
                  </div>
                  <div className="px-3 py-2 rounded" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                    <span style={{ fontSize: '0.75rem', color: '#16a34a' }}>Total summa:</span>
                    <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#15803d', marginLeft: '0.5rem' }}>
                      {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(filteredTotals.totalSum)}
                    </span>
                  </div>
                </div>
              </div>

              <Button
                variant="primary"
                onClick={() => history.push(`/${companyId}/create-invoices`)}
              >
                <Plus size={16} className="me-2" />
                Ny faktura
              </Button>
            </div>

            {refreshing && (
              <div className="text-center mb-3">
                <Loader2 size={16} className="me-1 spin" />
                Uppdaterar fakturlista...
              </div>
            )}

            {/* Enhanced table card */}
            <div className="card shadow-sm border-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
              <div className="card-body p-0">
                {/* Search filter */}
                <div className="p-4 border-bottom" style={{ backgroundColor: '#fafbfc' }}>
                  <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
                </div>

                <div className="table-responsive">
                  <table {...getTableProps()} className="table table-hover mb-0">
                    <thead style={{ backgroundColor: '#fff', borderBottom: '2px solid #f1f5f9' }}>
                      {headerGroups.map((headerGroup, headerGroupIndex) => (
                        <tr {...headerGroup.getHeaderGroupProps()} key={`header-group-${headerGroupIndex}`}>
                          {headerGroup.headers.map((column, columnIndex) => (
                            <th
                              {...column.getHeaderProps(column.getSortByToggleProps())}
                              key={`header-${columnIndex}-${column.id}`}
                              className="fw-semibold border-0 py-4 px-4"
                              style={{
                                cursor: column.canSort ? 'pointer' : 'default',
                                userSelect: 'none',
                                fontSize: '0.875rem',
                                letterSpacing: '0.025em',
                                color: '#1e293b',
                                backgroundColor: 'transparent'
                              }}
                            >
                              <div className="d-flex align-items-center justify-content-between">
                                <span>{column.render('Header')}</span>
                                {column.canSort && (
                                  <span className="ms-2">
                                    {column.isSorted ? (
                                      column.isSortedDesc ?
                                        <i className="fa fa-sort-down" style={{ fontSize: '0.75rem', color: '#3b82f6' }} />
                                        :
                                        <i className="fa fa-sort-up" style={{ fontSize: '0.75rem', color: '#3b82f6' }} />
                                    ) : (
                                      <i className="fa fa-sort" style={{ fontSize: '0.75rem', color: '#cbd5e1', opacity: '0.6' }} />
                                    )}
                                  </span>
                                )}
                              </div>
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                      {page.map((row) => {
                        prepareRow(row);
                        return (
                          <tr
                            {...row.getRowProps()}
                            key={row.id}
                            onClick={() => handleRowClick(row)}
                            style={{
                              cursor: "pointer",
                              transition: 'all 0.2s ease',
                              borderBottom: '1px solid #f1f5f9'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f8fafc';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            {row.cells.map(cell => (
                              <td
                                {...cell.getCellProps()}
                                key={cell.column.id}
                                className="py-4 px-4 align-middle border-0"
                                style={{
                                  fontSize: '0.875rem',
                                  color: '#374151'
                                }}
                              >
                                {cell.render('Cell')}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <PaginationControls />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default InvoiceList;
